/**
 * Supplier portal accounts.
 *
 * Pick a supplier, see who at that supplier can sign into the portal, and give
 * someone access. Creating an account produces an invitation link, shown here
 * for the operator to copy — it is deliberately not emailed automatically.
 */
define('portalUsersView',[
  'module',
  'marionette',
  'underscore',
  'app',
  'jquery',
  'backbone',
  'settings',
  'template!portalUsersTpl',
  'portalUsersListView',
  'portalUsersRoutingView',
  'portalImportsView',
  'portalOverviewView',
  'portalWaitingView',
  'portalConversationView',
  'portalOrdersView',
  'portalUserCreateView',
  'entities/suppliers/portal_users',
  'entities/suppliers/portal_imports',
  'entities/suppliers/portal_orders',
  'entities/caccounts/share'
], function (
  Module,
  Marionette,
  _,
  App,
  $,
  Backbone,
  Settings,
  viewTpl,
  PortalUsersListView,
  PortalUsersRoutingView,
  PortalImportsView,
  PortalOverviewView,
  PortalWaitingView,
  PortalConversationView,
  PortalOrdersView,
  PortalUserCreateView
) {
  'use strict';

  Module.exports = Marionette.LayoutView.extend({
    template: viewTpl,
    className: 'portal-users-view',

    regions: {
      imports: '.js-imports',
      waiting: '.js-waiting',
      overview: '.js-overview',
      orders: '.js-orders',
      routing: '.js-routing',
      list: '.js-list'
    },

    ui: {
      supplier: '.js-supplier',
      addUser: '.js-add-user',
      routing: '.js-routing'
    },

    events: {
      'change @ui.supplier': 'onSupplierChange',
      'click @ui.addUser': 'onAddUser'
    },

    initialize: function (options) {
      this.suppliers = options.suppliers || [];
      this.showAllSuppliers = false;
      // Open orders answer the question this screen raises; the closed ones are
      // history, and history is what the orders screen is for.
      this.orderScope = 'open';
      this.orderStart = 0;
    },

    serializeData: function () {
      return {suppliers: this.suppliers};
    },

    onShow: function () {
      this.showImports();
      this.showWaiting();
      this.showOverview();
      if (this.suppliers.length > 0) {
        this.onSupplierChange();
      }
      this.ui.addUser.toggle(this.suppliers.length > 0);
    },

    /**
     * The two scheduled imports, shown once for the whole account.
     *
     * Not reloaded when the supplier changes: one run of each covers every
     * supplier who has published, so there is nothing supplier-specific to
     * re-read. A scheduler that cannot be reached renders an empty section
     * saying so, rather than taking the accounts list down with it.
     */
    showImports: function () {
      $.when(App.request('portal-imports:get'))
        .done(_.bind(function (jobs) {
          this.getRegion('imports').show(new PortalImportsView({jobs: jobs || []}));
        }, this))
        .fail(_.bind(function () {
          this.getRegion('imports').show(new PortalImportsView({jobs: []}));
        }, this));
    },

    currentSupplierSecId: function () {
      return this.ui.supplier.val();
    },

    onSupplierChange: function () {
      this.showRouting();
      this.reload();
      // Page three of the previous supplier means nothing for this one.
      this.orderStart = 0;
      this.showOrders();
    },

    /**
     * This supplier's orders, in whichever scope is selected. The scope is kept
     * on the screen rather than per supplier: someone looking at closed orders
     * is doing so across suppliers, and resetting it on every pick would undo
     * their filter each time.
     */
    showOrders: function () {
      var supplierSecId = this.currentSupplierSecId();
      if (!supplierSecId) {
        return;
      }

      $.when(App.request('portal-users:orders', supplierSecId, this.orderScope, this.orderStart))
        .done(_.bind(function (page) {
          this.renderOrders(page || {}, false);
        }, this))
        .fail(_.bind(function () {
          this.renderOrders({}, true);
        }, this));
    },

    renderOrders: function (page, failed) {
      var view = new PortalOrdersView({
        orders: page.items || [],
        total: page.total || 0,
        start: page.start || 0,
        pageSize: page.pageSize,
        scope: this.orderScope,
        failed: failed
      });

      this.listenTo(view, 'scope', _.bind(function (scope) {
        this.orderScope = scope;
        // Back to the first page: the filter changed, so the offset is an
        // offset into a list that no longer exists.
        this.orderStart = 0;
        this.showOrders();
      }, this));

      this.listenTo(view, 'conversation', _.bind(this.openConversation, this));

      this.listenTo(view, 'page', _.bind(function (start) {
        this.orderStart = start;
        this.showOrders();
      }, this));

      this.getRegion('orders').show(view);
    },

    /**
     * Suppliers waiting on an answer, whichever supplier is picked below. A
     * failure shows nothing: the same conversations are still reachable from
     * each supplier's orders, so this list going missing must not raise an alarm
     * of its own.
     */
    showWaiting: function () {
      $.when(App.request('portal-orders:waiting'))
        .done(_.bind(function (orders) {
          this.renderWaiting(orders || []);
        }, this))
        .fail(_.bind(function () {
          this.renderWaiting([]);
        }, this));
    },

    renderWaiting: function (orders) {
      var view = new PortalWaitingView({orders: orders});
      this.listenTo(view, 'conversation', _.bind(this.openConversation, this));
      this.getRegion('waiting').show(view);
    },

    /**
     * Opening a conversation reads it, so both lists behind the popup are stale
     * the moment it closes: the waiting row should be gone and the badge on the
     * order with it.
     */
    openConversation: function (order) {
      var popup = new PortalConversationView({order: order});
      this.listenTo(popup, 'closed', _.bind(function () {
        this.showWaiting();
        this.showOrders();
      }, this));
      popup.show({
        title: _.i18n('portalMessages.title', null, 'Conversation with the supplier'),
        className: 'portal-conversation-modal'
      });
    },

    /**
     * The overview, refreshed whenever a setting changes so the row the operator
     * just edited reflects what they did — otherwise the table above would
     * contradict the card below it.
     */
    showOverview: function () {
      $.when(App.request('portal-users:overview', this.showAllSuppliers))
        .done(_.bind(function (rows) {
          this.renderOverview(rows || [], false);
        }, this))
        .fail(_.bind(function () {
          this.renderOverview([], true);
        }, this));
    },

    renderOverview: function (rows, failed) {
      var view = new PortalOverviewView({
        rows: rows,
        all: this.showAllSuppliers,
        failed: failed
      });

      this.listenTo(view, 'selected', _.bind(this.selectSupplier, this));
      this.listenTo(view, 'toggle:all', _.bind(function (all) {
        this.showAllSuppliers = all;
        this.showOverview();
      }, this));

      this.getRegion('overview').show(view);
    },

    /** A row is a shortcut to the picker, not a second way of holding state. */
    selectSupplier: function (supplierSecId) {
      if (!supplierSecId || this.currentSupplierSecId() === supplierSecId) {
        return;
      }
      this.ui.supplier.val(supplierSecId);
      this.onSupplierChange();
    },

    /**
     * The routing switch. Creating a login and routing orders are two separate
     * things — an account on a supplier who is not routed here signs in to an
     * empty queue — so the switch sits above the accounts, not hidden in a
     * settings page somewhere else.
     *
     * Read from the server rather than from the cached account config: the
     * dashboard loads that once at start-up, so it would go stale the moment
     * someone flips the switch.
     */
    showRouting: function () {
      var supplierSecId = this.currentSupplierSecId();
      if (!supplierSecId) {
        return;
      }

      $.when(App.request('portal-users:routing', supplierSecId))
        .done(_.bind(function (routing) {
          this.renderRouting(supplierSecId, routing);
        }, this))
        .fail(_.bind(function () {
          this.renderRouting(supplierSecId, null);
          this.showError(_.i18n('portalUsers.routingUnavailable', null,
            'Could not read whether this supplier is routed to the portal.'));
        }, this));
    },

    renderRouting: function (supplierSecId, routing) {
      var routingView = new PortalUsersRoutingView({
        supplierSecId: supplierSecId,
        routing: routing
      });

      // Keep the cached account config in step, so the order screen decides
      // correctly without making the operator reload the dashboard.
      this.listenTo(routingView, 'changed', _.bind(this.syncCachedConfig, this));
      this.listenTo(routingView, 'changed', _.bind(this.showOverview, this));

      this.getRegion('routing').show(routingView);
    },

    syncCachedConfig: function (routing) {
      var cAccountConfig = Settings.get('cAccountConfig');
      if (!cAccountConfig) {
        return;
      }
      var configs = cAccountConfig.get('supplierWSConfigs') || [];
      var config = _.findWhere(configs, {supplierSecId: routing.supplierSecId});

      if (config) {
        config.usePortal = routing.usePortal;
      } else {
        configs.push({
          supplierSecId: routing.supplierSecId,
          usePortal: routing.usePortal
        });
      }
      cAccountConfig.set('supplierWSConfigs', configs);
    },

    reload: function () {
      var supplierSecId = this.currentSupplierSecId();
      if (!supplierSecId) {
        return;
      }

      $.when(App.request('portal-users:get', supplierSecId))
        .done(_.bind(function (users) {
          this.showList(users || []);
        }, this))
        .fail(_.bind(function () {
          this.showList([]);
        }, this));
    },

    showList: function (users) {
      var listView = new PortalUsersListView({
        collection: new Backbone.Collection(users)
      });

      this.listenTo(listView, 'changed', _.bind(this.reload, this));
      this.listenTo(listView, 'invite:issued', _.bind(this.showInvite, this));
      this.listenTo(listView, 'failed', _.bind(function () {
        this.showError(_.i18n('portalUsers.actionFailed', null,
          'That did not go through. Try again in a moment.'));
      }, this));

      this.getRegion('list').show(listView);
    },

    onAddUser: function () {
      var supplierSecId = this.currentSupplierSecId();
      if (!supplierSecId) {
        return;
      }

      var popup = new PortalUserCreateView({supplierSecId: supplierSecId});
      this.listenTo(popup, 'created', _.bind(function (response) {
        this.reload();
        this.showInvite(response);
      }, this));
      popup.show({
        title: _.i18n('portalUsers.new', null, 'New portal account')
      });
    },

    /** The link only exists in this response — show it until it is copied. */
    showInvite: function (response) {
      if (!response || !response.inviteUrl) {
        return;
      }
      this.$('.js-invite-banner').remove();

      var $banner = $(
        '<div class="invite-banner js-invite-banner">' +
        '  <span class="invite-banner__label"></span>' +
        '  <input type="text" class="form-control invite-banner__url js-invite-url" readonly/>' +
        '  <button type="button" class="btn btn-sm js-copy-invite"></button>' +
        '</div>'
      );
      $banner.find('.invite-banner__label').text(
        _.i18n('portalUsers.inviteReady', null, 'Invitation link, valid 72 hours — send it to') +
        ' ' + (response.user ? response.user.email : ''));
      $banner.find('.js-invite-url').val(response.inviteUrl);
      $banner.find('.js-copy-invite').text(_.i18n('common.copy', null, 'Copy'));

      $banner.find('.js-copy-invite').on('click', function () {
        var $input = $banner.find('.js-invite-url');
        $input.select();
        document.execCommand('copy');
        $(this).text(_.i18n('common.copied', null, 'Copied'));
      });

      this.ui.routing.after($banner);
    },

    showError: function (message) {
      this.$('.js-error').remove();
      this.ui.routing.after($('<div class="portal-users-error js-error"></div>').text(message));
    }
  });
});

